import axios from 'axios';
import { isNumber } from 'lodash';
import { logger } from '@sm360/logger';
import config from '../../config/index.js';
import ValidationService from '../ValidationService/ValidationService.js';

/**
 * Call to send a Standard Email for the new TradeIn widget
 * @param {Number} [websiteId] The website we're sending the email from
 * @param {String} [locale] The website language
 * @param {String} [clientName] The client full name
 * @param {Number} [year] The TradeIn vehicle year
 * @param {String} [make] The TradeIn vehicle make
 * @param {String} [model] The TradeIn vehicle model
 * @param {String} [trim] The TradeIn vehicle trim
 * @param {String} [vin] The TradeIn vehicle VIN
 * @param {String} [exteriorColor] The TradeIn vehicle exteriorColor
 * @param {Number} [amount] The TradeIn vehicle appraisal amount
 * @param {String} [lowAmount] The TradeIn vehicle min appraisal amount
 * @param {String} [highAmount] The TradeIn vehicle max appraisal amount
 * @param {String} [amountPercentage] The TradeIn vehicle percentage between min and max
 * @param {String} [toEmail] Email recipient
 * @param {Array}  [conditionFormList] Array of questions/answers/indent as follow [{question:"test", answer:"test", indent: true}]
 * @param {Array}  [vehicleOptionsList] Array of vehicle options as follow [{code:"test", description:"test"}]
 * @param {String} [organizationUnitName] Dealership name
 * @param {String} [websiteDomain] Dealership website
 * @param {String} [websiteLogo] Dealership logo
 * @param {String} [organizationUnitPhone] Dealership phone
 * @param {String} [websiteFacebookUrl] Dealership Social Media (Facebook)
 * @param {String} [websiteInstagramUrl] Dealership Social Media (Instagram)
 * @param {String} [websiteTwitterUrl] Dealership Social Media (Twitter)
 * @param {String} [websiteYoutubeUrl] Dealership Social Media (YouTube)
 */
const sendTradeInStandardEmail = ({
    websiteId,
    locale,
    clientName,
    year,
    make,
    model,
    trim,
    vin,
    exteriorColor,
    amount,
    toEmail,
    conditionFormList,
    organizationUnitName,
    organizationUnitPhone,
    websiteDomain,
    websiteLogo,
    websiteFacebookUrl,
    websiteInstagramUrl,
    websiteTwitterUrl,
    websiteYoutubeUrl,
    lowAmount,
    highAmount,
    vehicleOptionsList,
    amountPercentage,
}) => {
    if (!ValidationService.validateEmail(toEmail)) return Promise.reject('Not valid email');
    if (!isNumber(websiteId)) return Promise.reject('Website Id is not valid');

    const url = `${config.endpoint.widget}/widgets/email/tradeIn`;
    const body = {
        websiteId,
        clientName,
        year,
        make,
        model,
        trim,
        vin,
        exteriorColor,
        amount,
        toEmail,
        conditionFormList,
        organizationUnitName,
        organizationUnitPhone,
        websiteDomain,
        websiteLogo,
        websiteFacebookUrl,
        websiteInstagramUrl,
        websiteTwitterUrl,
        websiteYoutubeUrl,
        lowAmount,
        highAmount,
        vehicleOptionsList,
        amountPercentage,
    };
    return axios
        .post(url, body, { headers: { Locale: locale } })
        .then(({ data }) => {
            if (data.error) {
                logger.warn(`An error occurred while sending the standard email to ${toEmail}`, data.error);
                return Promise.reject(data.error);
            }
            return Promise.resolve(data);
        })
        .catch((err) => {
            logger.error(`An error occurred while sending the standard email to ${toEmail}`, err);
            return Promise.reject(err);
        });
};

/**
 * Call to send a Cheque Email for the new TradeIn widget
 * @param {Number} [websiteId] The website we're sending the email from
 * @param {String} [locale] The website language
 * @param {String} [leadId] The lead/certificate ID
 * @param {String} [clientName] The client full name
 * @param {Number} [year] The TradeIn vehicle year
 * @param {String} [make] The TradeIn vehicle make
 * @param {String} [model] The TradeIn vehicle model
 * @param {String} [trim] The TradeIn vehicle trim
 * @param {String} [vin] The TradeIn vehicle VIN
 * @param {String} [exteriorColor] The TradeIn vehicle exteriorColor
 * @param {Number} [amount] The TradeIn vehicle appraisal amount
 * @param {String} [amountInLetters] The TradeIn vehicle appraisal amount written in letters
 * @param {String} [toEmail] Email recipient
 * @param {Array}  [conditionFormList] Array of questions/answers/indent as follow [{question:"test", answer:"test", indent: true}]
 * @param {String} [organizationUnitName] Dealership name
 * @param {String} [organizationUnitPhone] Dealership phone
 * @param {String} [scheduleMonday] opening hour of Monday
 * @param {String} [scheduleTuesday] opening hour of Tuesday
 * @param {String} [scheduleWednesday] opening hour of Wednesday
 * @param {String} [scheduleThursday] opening hour of Thursday
 * @param {String} [scheduleFriday] opening hour of Friday
 * @param {String} [scheduleSaturday] opening hour of Saturday
 * @param {String} [scheduleSunday] opening hour of Sunday
 * @param {String} [websiteDomain] Dealership website
 * @param {String} [websiteLogo] Dealership logo
 * @param {String} [websiteFacebookUrl] Dealership Social Media (Facebook)
 * @param {String} [websiteInstagramUrl] Dealership Social Media (Instagram)
 * @param {String} [websiteTwitterUrl] Dealership Social Media (Twitter)
 * @param {String} [websiteYoutubeUrl] Dealership Social Media (YouTube)
 */
const sendTradeInChequeEmail = ({
    websiteId,
    locale,
    leadId,
    clientName,
    year,
    make,
    model,
    trim,
    vin,
    exteriorColor,
    amount,
    amountInLetters,
    conditionFormList,
    toEmail,
    organizationUnitName,
    organizationUnitPhone,
    scheduleMonday,
    scheduleTuesday,
    scheduleWednesday,
    scheduleThursday,
    scheduleFriday,
    scheduleSaturday,
    scheduleSunday,
    websiteDomain,
    websiteLogo,
    websiteFacebookUrl,
    websiteInstagramUrl,
    websiteTwitterUrl,
    websiteYoutubeUrl,
}) => {
    if (!ValidationService.validateEmail(toEmail)) return Promise.reject('Not valid email');
    if (!isNumber(websiteId)) return Promise.reject('Website Id is not valid');

    const url = `${config.endpoint.widget}/widgets/email/tradeInCheque`;
    const body = {
        websiteId,
        leadId,
        clientName,
        year,
        make,
        model,
        trim,
        vin,
        exteriorColor,
        amount,
        amountInLetters,
        conditionFormList,
        toEmail,
        organizationUnitName,
        organizationUnitPhone,
        scheduleMonday,
        scheduleTuesday,
        scheduleWednesday,
        scheduleThursday,
        scheduleFriday,
        scheduleSaturday,
        scheduleSunday,
        websiteDomain,
        websiteLogo,
        websiteFacebookUrl,
        websiteInstagramUrl,
        websiteTwitterUrl,
        websiteYoutubeUrl,
    };
    return axios
        .post(url, body, { headers: { Locale: locale } })
        .then(({ data }) => {
            if (data.error) {
                logger.warn(`An error occurred while sending the cheque email to ${toEmail}`, data.error);
                return Promise.reject(data.error);
            }
            return Promise.resolve(data);
        })
        .catch((err) => {
            logger.error(`An error occurred while sending the standard email to ${toEmail}`, err);
            return Promise.reject(err);
        });
};

const EmailService = {
    sendTradeInStandardEmail,
    sendTradeInChequeEmail,
};

export default EmailService;
